export type Eip1193Provider = {
  request(args: { method: string; params?: unknown[] | object }): Promise<unknown>;
  on?: (event: string, listener: (...args: unknown[]) => void) => void;
  removeListener?: (event: string, listener: (...args: unknown[]) => void) => void;
  connect?: () => Promise<unknown>;
  disconnect?: () => Promise<void>;
};

export type DiscoveredWallet = {
  id: string;
  name: string;
  icon?: string;
  rdns?: string;
  provider: Eip1193Provider;
  source: "eip6963" | "wallet-fallback" | "legacy";
};

type Eip6963Detail = {
  info: { uuid: string; name: string; icon: string; rdns: string };
  provider: Eip1193Provider;
};

declare global {
  interface Window {
    ethereum?: Eip1193Provider;
    okxwallet?: Eip1193Provider & { ethereum?: Eip1193Provider };
    bitkeep?: { ethereum?: Eip1193Provider };
  }
}

function canonicalId(name: string, rdns?: string, uuid?: string) {
  const haystack = `${name} ${rdns ?? ""}`.toLowerCase();
  if (haystack.includes("okx")) return "okx";
  const normalizedRdns = rdns?.trim().toLowerCase();
  if (normalizedRdns) return normalizedRdns;
  return uuid || name.toLowerCase().replace(/[^a-z0-9]+/g, "-");
}

function dedupeWallets(wallets: DiscoveredWallet[]) {
  const unique: DiscoveredWallet[] = [];
  for (const wallet of wallets) {
    const sameProvider = unique.findIndex((candidate) => candidate.provider === wallet.provider);
    const sameIdentity = unique.findIndex((candidate) => candidate.id === wallet.id);
    const index = sameProvider >= 0 ? sameProvider : sameIdentity;
    if (index < 0) {
      unique.push(wallet);
      continue;
    }

    const existing = unique[index]!;
    // EIP-6963 metadata wins over hand-written fallbacks for the same wallet.
    if (existing.source !== "eip6963" && wallet.source === "eip6963") unique[index] = wallet;
    else if (!existing.icon && wallet.icon) unique[index] = wallet;
  }

  const standardsBased = unique.some((wallet) => wallet.source === "eip6963");
  return standardsBased ? unique.filter((wallet) => wallet.source !== "legacy") : unique;
}

export function discoverWallets(onChange: (wallets: DiscoveredWallet[]) => void): () => void {
  const found = new Map<string, DiscoveredWallet>();
  let fallbackTimer = 0;
  const emit = () => onChange(dedupeWallets([...found.values()]));

  const onAnnounce = (event: Event) => {
    const detail = (event as CustomEvent<Eip6963Detail>).detail;
    if (!detail?.provider || !detail.info) return;
    const id = canonicalId(detail.info.name, detail.info.rdns, detail.info.uuid);
    found.set(id, {
      id,
      name: detail.info.name,
      icon: detail.info.icon,
      rdns: detail.info.rdns,
      provider: detail.provider,
      source: "eip6963"
    });
    emit();
  };

  window.addEventListener("eip6963:announceProvider", onAnnounce);
  window.dispatchEvent(new Event("eip6963:requestProvider"));

  // Some in-app wallets expose a dedicated provider even when they do not announce EIP-6963.
  const okx = window.okxwallet?.ethereum ?? window.okxwallet;
  if (okx && !found.has("okx")) {
    found.set("okx", { id: "okx", name: "OKX Wallet", provider: okx, source: "wallet-fallback" });
    emit();
  }

  // Bitget exposes a dedicated EVM provider in its app/extension even when EIP-6963 is absent.
  const bitget = window.bitkeep?.ethereum;
  if (bitget && !found.has("bitget")) {
    found.set("bitget", { id: "bitget", name: "Bitget Wallet", provider: bitget, source: "wallet-fallback" });
    emit();
  }

  // Give EIP-6963 announcements a short window before exposing the ambiguous window.ethereum fallback.
  fallbackTimer = window.setTimeout(() => {
    const hasStandardsProvider = [...found.values()].some((wallet) => wallet.source === "eip6963");
    if (!hasStandardsProvider && !okx && !bitget && window.ethereum) {
      found.set("legacy-injected", { id: "legacy-injected", name: "Browser Wallet", provider: window.ethereum, source: "legacy" });
      emit();
    }
  }, 300);

  return () => {
    window.clearTimeout(fallbackTimer);
    window.removeEventListener("eip6963:announceProvider", onAnnounce);
  };
}

const AUTHORIZED_WALLETS_KEY = "scc:authorizedWallets";
const MANUAL_DISCONNECT_KEY = "scc:manualDisconnect";

export function readAuthorizedWalletIds(): string[] {
  try {
    const parsed = JSON.parse(localStorage.getItem(AUTHORIZED_WALLETS_KEY) ?? "[]");
    return Array.isArray(parsed) ? parsed.filter((value): value is string => typeof value === "string") : [];
  } catch {
    return [];
  }
}

export function markWalletAuthorized(walletId: string) {
  try {
    const next = [walletId, ...readAuthorizedWalletIds().filter((value) => value !== walletId)].slice(0, 30);
    localStorage.setItem(AUTHORIZED_WALLETS_KEY, JSON.stringify(next));
    localStorage.removeItem(MANUAL_DISCONNECT_KEY);
  } catch {
    // Storage is only used to decide whether silent reconnect is appropriate.
  }
}

export function wasWalletAuthorizedByUser(walletId: string, preferredWalletId?: string) {
  // preferredWalletId is a safe migration signal from v0.2.0: it was only written after an explicit Connect action.
  return readAuthorizedWalletIds().includes(walletId) || preferredWalletId === walletId;
}

export function setManualDisconnect(value: boolean) {
  try {
    if (value) localStorage.setItem(MANUAL_DISCONNECT_KEY, "1");
    else localStorage.removeItem(MANUAL_DISCONNECT_KEY);
  } catch {
    // noop
  }
}

export function isManuallyDisconnected() {
  try {
    return localStorage.getItem(MANUAL_DISCONNECT_KEY) === "1";
  } catch {
    return false;
  }
}
